import React, { useState, useContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Chip,
  Checkbox,
} from '@material-ui/core';
import { SearchOutlined, SendOutlined } from '@material-ui/icons';
import { AppContext } from '../../modules/AppContext';
import FCGrid from '../../components/FCGrid';
import { LoaderSubmitButton } from '../../components/LoaderSubmitButton';
import { integratedTransitionApi } from '../../modules/IntegratedTransition/IntegratedTransition';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
  content: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, 0),
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  fieldGroupLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  participantIdsField: {
    minWidth: 280,
  },
  validationList: {
    listStyle: 'none',
    padding: 0,
    margin: theme.spacing(2, 0),
  },
  validationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  problemsBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  problemsList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
}));

const STATUS_COLORS = {
  Managed: '#4caf50',
  default: '#f44336',
};

const getStatusColor = (status) =>
  STATUS_COLORS[status] || STATUS_COLORS.default;

const parseParticipantIds = (text) => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const ids = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!/^\d+$/.test(line)) {
      return {
        error: `Line ${i + 1}: enter a single integer per line.`,
        ids: [],
      };
    }
    ids.push(parseInt(line, 10));
  }
  return { error: null, ids };
};

export const CoreToIntegratedTransition = () => {
  const { showToast } = useContext(AppContext);
  const classes = useStyles();

  const [oldPlanSponsorId, setOldPlanSponsorId] = useState('');
  const [newPlanSponsorId, setNewPlanSponsorId] = useState('');
  const [participantIdsText, setParticipantIdsText] = useState('');

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [submitProblems, setSubmitProblems] = useState([]);

  const validation = useMemo(() => {
    if (participants.length === 0) {
      return {
        valid: false,
        allManaged: false,
        anyMatched: false,
        noDuplicates: false,
      };
    }

    const allManaged = participants.every((p) => p.oldStatus === 'Managed');
    const anyMatched = participants.some(
      (p) => p.selected && p.newParticipantId != null
    );
    const noSelectedUnmatched = !participants.some(
      (p) => p.selected && p.newParticipantId == null
    );

    const selectedIds = participants
      .filter((p) => p.selected)
      .map((p) => p.newParticipantId)
      .filter(Boolean);
    const noDuplicates = selectedIds.length === new Set(selectedIds).size;

    return {
      valid: allManaged && anyMatched && noSelectedUnmatched && noDuplicates,
      allManaged,
      anyMatched,
      noSelectedUnmatched,
      noDuplicates,
    };
  }, [participants]);

  const oldPlanSponsorIdValid =
    oldPlanSponsorId.trim() !== '' && /^\d+$/.test(oldPlanSponsorId.trim());
  const newPlanSponsorIdValid =
    newPlanSponsorId.trim() !== '' && /^\d+$/.test(newPlanSponsorId.trim());
  const canSearch = oldPlanSponsorIdValid && newPlanSponsorIdValid;

  const handleFindNewParticipants = async () => {
    const parsed = parseParticipantIds(participantIdsText);
    if (parsed.error) {
      showToast({ message: parsed.error, type: 'error' });
      return;
    }
    if (parsed.ids.length === 0) {
      showToast({
        message: 'Enter at least one participant ID (one per line).',
        type: 'error',
      });
      return;
    }

    setLoading(true);
    setParticipants([]);
    setFetched(false);

    try {
      const findNewRes = await integratedTransitionApi.findNewParticipants({
        oldPlanSponsorId: parseInt(oldPlanSponsorId.trim(), 10),
        newPlanSponsorId: parseInt(newPlanSponsorId.trim(), 10),
        participantIds: parsed.ids,
      });

      if (!findNewRes.ok) {
        showToast({
          message: 'Failed to find new participants',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const newParticipants = await findNewRes.json();

      const combined = (
        Array.isArray(newParticipants) ? newParticipants : []
      ).map((item, index) => {
        const matches =
          item.matchingParticipants ?? item.MatchingParticipants ?? [];
        const firstMatch = matches[0];
        const newId =
          matches.length === 1
            ? firstMatch?.participantId ?? firstMatch?.ParticipantId ?? null
            : null;
        return {
          id: index,
          selected: matches.length > 0,
          oldParticipantId:
            item.participant?.participantId ?? item.Participant?.ParticipantId,
          firstName:
            item.participant?.firstName ?? item.Participant?.FirstName ?? '',
          lastName:
            item.participant?.lastName ?? item.Participant?.LastName ?? '',
          last4: item.participant?.last4 ?? item.Participant?.Last4 ?? '',
          dateOfBirth:
            item.participant?.dateOfBirth ?? item.Participant?.DateOfBirth,
          oldStatus:
            item.participant?.isManaged ?? item.Participant?.IsManaged
              ? 'Managed'
              : 'Not Managed',
          newParticipantId: newId,
          matchingParticipants: matches,
        };
      });

      setParticipants(combined);
      setFetched(true);
      showToast({
        message: `Found ${combined.length} participants`,
        type: 'success',
      });
    } catch (err) {
      showToast({ message: `Error: ${err.message}`, type: 'error' });
    }

    setLoading(false);
  };

  const handleSubmitTransition = async () => {
    setSubmitting(true);
    setSubmitProblems([]);

    try {
      const payload = participants
        .filter((p) => p.selected && p.newParticipantId != null)
        .map((p) => ({
          oldParticipantId: p.oldParticipantId,
          newParticipantId: p.newParticipantId,
        }));

      const res = await integratedTransitionApi.submitTransition(payload);
      const data = await res.json().catch(() => null);
      const problems = Array.isArray(data) ? data : [];

      if (res.ok) {
        if (problems.length === 0) {
          showToast({
            message: 'Transition submitted successfully',
            type: 'success',
          });
          setParticipants([]);
          setFetched(false);
        } else {
          setSubmitProblems(problems);
          const reasonList = problems
            .map(
              (p) =>
                `Old ${p.oldParticipantId ?? p.OldParticipantId} → New ${
                  p.newParticipantId ?? p.NewParticipantId
                }: ${p.reason ?? p.Reason ?? 'Unknown'}`
            )
            .join('; ');
          showToast({
            message: `Some transitions failed (${problems.length}). See details below.`,
            type: 'error',
          });
        }
      } else {
        showToast({
          message:
            data?.message || data?.Message || 'Failed to submit transition',
          type: 'error',
        });
      }
    } catch (err) {
      showToast({ message: `Error: ${err.message}`, type: 'error' });
    }

    setSubmitting(false);
  };

  const handleToggleSelected = (id) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p))
    );
  };

  const handleToggleAll = () => {
    const eligible = participants.filter(
      (p) => (p.matchingParticipants || []).length > 0
    );
    const allSelected = eligible.every((p) => p.selected);
    setParticipants((prev) =>
      prev.map((p) =>
        (p.matchingParticipants || []).length === 0
          ? p
          : { ...p, selected: !allSelected }
      )
    );
  };

  const handleReset = () => {
    setParticipants([]);
    setFetched(false);
    setSubmitProblems([]);
  };

  const handleClear = () => {
    setParticipants([]);
    setFetched(false);
    setSubmitProblems([]);
    setOldPlanSponsorId('');
    setNewPlanSponsorId('');
    setParticipantIdsText('');
  };

  const formatDob = (dob) =>
    dob ? new Date(dob).toLocaleDateString('en-US') : '—';

  const eligible = participants.filter(
    (p) => (p.matchingParticipants || []).length > 0
  );
  const allSelected = eligible.length > 0 && eligible.every((p) => p.selected);
  const someSelected = eligible.some((p) => p.selected);

  const SelectAllHeader = () => (
    <Checkbox
      size="small"
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      onChange={handleToggleAll}
    />
  );

  const columnDefs = [
    {
      headerComponent: SelectAllHeader,
      field: 'selected',
      sortable: false,
      suppressSizeToFit: true,
      width: 80,
      minWidth: 80,
      cellRenderer: (params) => (
        <Checkbox
          size="small"
          checked={!!params.value}
          disabled={(params.data.matchingParticipants || []).length === 0}
          onChange={() => handleToggleSelected(params.data.id)}
        />
      ),
    },
    {
      headerName: 'Current (Old) Participant',
      children: [
        {
          headerName: 'Participant ID',
          field: 'oldParticipantId',
          sortable: true,
        },
        {
          headerName: 'Name',
          field: 'firstName',
          sortable: true,
          minWidth: 200,
          valueGetter: (params) =>
            `${params.data.firstName || ''} ${
              params.data.lastName || ''
            }`.trim() || '—',
        },
        {
          headerName: 'Last 4',
          field: 'last4',
          sortable: true,
        },
        {
          headerName: 'DOB',
          field: 'dateOfBirth',
          sortable: true,
          valueFormatter: (params) => formatDob(params.value),
        },
        {
          headerName: 'Status',
          field: 'oldStatus',
          sortable: true,
          cellRenderer: (params) => (
            <Chip
              size="small"
              label={params.value}
              style={{
                backgroundColor: getStatusColor(params.value),
                color: '#fff',
              }}
            />
          ),
        },
      ],
    },
    {
      headerName: 'New Participant',
      children: [
        {
          colId: 'newParticipantId',
          headerName: 'Participant ID',
          sortable: false,
          valueGetter: (params) => {
            const matches = params.data.matchingParticipants || [];
            if (matches.length === 0) return '—';
            const match = matches[0] || {};
            return match.participantId ?? match.ParticipantId ?? '—';
          },
        },
        {
          colId: 'newParticipantName',
          headerName: 'Name',
          sortable: false,
          minWidth: 200,
          valueGetter: (params) => {
            const matches = params.data.matchingParticipants || [];
            if (matches.length === 0) return '—';
            const match = matches[0] || {};
            const first = match.firstName ?? match.FirstName ?? '';
            const last = match.lastName ?? match.LastName ?? '';
            return [first, last].filter(Boolean).join(' ') || '—';
          },
        },
        {
          colId: 'newParticipantLast4',
          headerName: 'Last 4',
          sortable: false,
          valueGetter: (params) => {
            const matches = params.data.matchingParticipants || [];
            if (matches.length === 0) return '—';
            const match = matches[0] || {};
            return match.last4 ?? match.Last4 ?? '—';
          },
        },
        {
          colId: 'newParticipantDob',
          headerName: 'DOB',
          sortable: false,
          valueGetter: (params) => {
            const matches = params.data.matchingParticipants || [];
            if (matches.length === 0) return '—';
            const match = matches[0] || {};
            return formatDob(match.dateOfBirth ?? match.DateOfBirth);
          },
        },
      ],
    },
    {
      colId: 'matchStatus',
      headerName: 'Match Status',
      sortable: false,
      cellRenderer: (params) => {
        const row = params.data;
        const matches = row.matchingParticipants || [];
        if (matches.length === 0)
          return (
            <Chip
              size="small"
              label="No Match"
              style={{ backgroundColor: '#f44336', color: '#fff' }}
            />
          );
        return (
          <Chip
            size="small"
            label={row.newParticipantId != null ? 'Matched' : 'Unselected'}
            style={{
              backgroundColor:
                row.newParticipantId != null ? '#4caf50' : '#ff9800',
              color: '#fff',
            }}
          />
        );
      },
    },
  ];

  const ValidationIndicator = ({ passed, label }) => (
    <li className={classes.validationItem}>
      <Chip
        size="small"
        label={passed ? '✓' : '✗'}
        style={{
          backgroundColor: passed ? '#4caf50' : '#f44336',
          color: '#fff',
          fontWeight: 'bold',
          minWidth: 28,
        }}
      />
      <Typography variant="body2">{label}</Typography>
    </li>
  );

  return (
    <div className={classes.wrapper}>
      <Paper elevation={1} className={classes.content}>
        <Typography variant="h6">
          Core To Integrated Transition Utility
        </Typography>

        <div className={classes.formGrid}>
          <div className={classes.fieldGroup}>
            <Typography variant="subtitle2" className={classes.fieldGroupLabel}>
              Plan sponsor IDs
            </Typography>
            <TextField
              label="Old Plan Sponsor ID"
              variant="outlined"
              size="small"
              value={oldPlanSponsorId}
              onChange={(e) => setOldPlanSponsorId(e.target.value)}
              disabled={fetched}
              required
              placeholder="e.g. 221525"
            />
            <TextField
              label="New Plan Sponsor ID"
              variant="outlined"
              size="small"
              value={newPlanSponsorId}
              onChange={(e) => setNewPlanSponsorId(e.target.value)}
              disabled={fetched}
              required
              placeholder="e.g. 221619"
            />
          </div>

          <div className={classes.fieldGroup}>
            <Typography variant="subtitle2" className={classes.fieldGroupLabel}>
              Participant IDs (one per line)
            </Typography>
            <TextField
              className={classes.participantIdsField}
              variant="outlined"
              size="small"
              multiline
              minRows={4}
              maxRows={12}
              placeholder="e.g. 4582559"
              value={participantIdsText}
              onChange={(e) => setParticipantIdsText(e.target.value)}
              disabled={fetched}
            />
          </div>
        </div>

        <Box display="flex" justifyContent="flex-end" gridGap="1rem" mt={3}>
          <LoaderSubmitButton
            variant="outlined"
            onClick={handleClear}
            disabled={loading || submitting}
          >
            Clear
          </LoaderSubmitButton>
          {fetched && (
            <LoaderSubmitButton
              variant="outlined"
              onClick={handleReset}
              disabled={loading || submitting}
            >
              Reset
            </LoaderSubmitButton>
          )}
          <LoaderSubmitButton
            icon={<SearchOutlined />}
            variant="contained"
            color="primary"
            disabled={!canSearch || loading || fetched}
            loading={loading}
            onClick={handleFindNewParticipants}
          >
            Find new participants
          </LoaderSubmitButton>
        </Box>
      </Paper>

      {participants.length > 0 && (
        <section className={classes.mainContent}>
          <Paper elevation={1} className={classes.content}>
            <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
              Participant Mapping ({participants.length} participants)
            </Typography>
            <FCGrid columnDefs={columnDefs} rowData={participants} search />
          </Paper>
          <Paper elevation={1} className={classes.content}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Prerequisites for Submit for Transition
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 8 }}
            >
              All of the following must be satisfied for the button to be
              enabled:
            </Typography>
            <ul className={classes.validationList}>
              <ValidationIndicator
                passed={validation.allManaged}
                label="All old participants are currently Managed"
              />
              <ValidationIndicator
                passed={validation.anyMatched}
                label="At least one participant has been matched"
              />
              <ValidationIndicator
                passed={validation.noSelectedUnmatched}
                label="All selected participants have a match chosen (none selected without a match)"
              />
              <ValidationIndicator
                passed={validation.noDuplicates}
                label="No new participant is selected more than once"
              />
            </ul>
            <Box display="flex" justifyContent="flex-end">
              <LoaderSubmitButton
                icon={<SendOutlined />}
                variant="contained"
                color="primary"
                disabled={!validation.valid || submitting}
                loading={submitting}
                onClick={handleSubmitTransition}
              >
                Submit for Transition
              </LoaderSubmitButton>
            </Box>
            {submitProblems.length > 0 && (
              <Box className={classes.problemsBox}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Transitions that failed (returned by server)
                </Typography>
                <ul className={classes.problemsList}>
                  {submitProblems.map((p, i) => (
                    <li key={i}>
                      <Typography variant="body2">
                        Old participant{' '}
                        {p.oldParticipantId ?? p.OldParticipantId} → New
                        participant {p.newParticipantId ?? p.NewParticipantId}:{' '}
                        {p.reason ?? p.Reason ?? 'Unknown'}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Paper>
        </section>
      )}

      {fetched && participants.length === 0 && (
        <Paper elevation={1} className={classes.content}>
          <Typography variant="body1" color="textSecondary" align="center">
            No participants found for the given IDs and plan sponsors.
          </Typography>
        </Paper>
      )}
    </div>
  );
};
